import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
// Importação de CSS consolidada em blocos para evitar chunking excessivo
import "./assets/external-css/grid.css";
import "./assets/external-css/bootstrap_nav_tables.css";
import "./assets/external-css/reset.css";
import "./index.css";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { BrowserRouter } from "react-router-dom";
import i18n from "./i18n";
import { I18nextProvider } from "react-i18next";
import { createStore, applyMiddleware, compose, combineReducers } from "redux";
import { Provider } from "react-redux";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import createSagaMiddleware from "redux-saga";
import myUsersReducer from "./store/reducers/_ApiMyUsers";
import codeReducer from "./store/reducers/_ApiCode";
import tasksReducer from "./store/reducers/_ApiTasks";
import plansReducer from "./store/reducers/_ApiPlans";
import productsReducer from "./store/reducers/ApiProducts";
import statisticsReducer from "./store/reducers/_ApiStatistics";
import activitiesReducer from "./store/reducers/_StaticActivities";
import contentsReducer from "./store/reducers/_StaticContents";
import sessionReducer from "./store/reducers/_LocalSession";
import filtersReducer from "./store/reducers/_LocalFilters";
import configsReducer from "./store/reducers/_ConfigsReducer.js";
import newFiltersReducer from "./store/reducers/_newFiltersReducer";
import * as sagas from "./store/sagas";
import { PersistGate } from "redux-persist/integration/react";
import _PurchaseReducer from "./store/reducers/_PurchaseReducer";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

// 🛡️ Importa o Sentry
import * as Sentry from "@sentry/react";

if (process.env.REACT_APP_STAGE === "prod") {
  // 🛡️ Inicializa o Sentry antes de tudo
  Sentry.init({
    dsn: "https://33ab44ec92f011fab5dcab00d602604a@o4509271999643648.ingest.us.sentry.io/4509277426221056",
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const errorMessage = error?.message || error?.toString();

      // Lista de erros a serem ignorados
      const ignoredErrors = [
        "FirebaseError",
        "SecurityError",
        "NS_ERROR_FAILURE",
        "AxiosError: Network Error",
        "AxiosError",
      ];

      if (
        errorMessage &&
        ignoredErrors.some((ignored) => errorMessage.includes(ignored))
      ) {
        return null;
      }

      return event;
    },
    // Session Replay
    replaysSessionSampleRate: 0.0, // 0% - NÃO gravar sessões normais
    replaysOnErrorSampleRate: 1.0, // 100% de chande de gravar replay quando houver erro
    tracesSampleRate: 0.0, // 0% - NÃO capturar traces de performance
    tracePropagationTargets: [process.env.PROD_URL], // URL do servidor de produção
  });
}

// ---- REDUX, STORE, PERSIST, etc ----
const composeEnhancers =
  process.env.NODE_ENV === "development" &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose;

const rootReducer = combineReducers({
  contents: contentsReducer,
  tasks: tasksReducer,
  plans: plansReducer,
  products: productsReducer,
  activities: activitiesReducer,
  statistics: statisticsReducer,
  myUsers: myUsersReducer,
  filters: filtersReducer,
  newFilters: newFiltersReducer,
  // statistics: statsReducer,
  // plans: plansReducer,
  session: sessionReducer,
  code: codeReducer,
  configs: configsReducer,
  purchase: _PurchaseReducer,
});

const SagaMiddleware = createSagaMiddleware();

// Configuração de persistência aprimorada para iOS
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["filters", "configs", "purchase", "newFilters"],
  transforms: [
    {
      out: (state) => {
        const now = Date.now();
        if (
          state.newFilters.expirationTime &&
          state.newFilters.expirationTime < now
        ) {
          return {
            ...state,
            newFilters: { filterData: null, expirationTime: null },
          };
        }
        return state;
      },
      in: (state) => state,
    },
  ],
  // Adicionando timeout para evitar problemas no Safari/iOS
  timeout: 10000,
  // Estratégias de fallback para iOS
  writeFailHandler: (err) => {
    console.warn("Redux persist write failed:", err);
    // Não falhar completamente - apenas log do erro
  },
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = createStore(
  persistedReducer,
  composeEnhancers(applyMiddleware(SagaMiddleware)),
);

// Sagas
SagaMiddleware.run(sagas.watchMyUsers);
SagaMiddleware.run(sagas.watchActivities);
SagaMiddleware.run(sagas.watchContents);
SagaMiddleware.run(sagas.watchSession);
SagaMiddleware.run(sagas.watchCode);
SagaMiddleware.run(sagas.watchTasks);
SagaMiddleware.run(sagas.watchPlans);
SagaMiddleware.run(sagas.watchProducts);
SagaMiddleware.run(sagas.watchStatistics);

// ---- MONTAGEM DO APP ----
const persistor = persistStore(store);
const queryClient = new QueryClient();
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});
const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const persistOptions = {
  persister: localStoragePersister,
  maxAge: ONE_DAY_MS,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      // Salva apenas requisições com sucesso e que tenham a flag persist: true
      return query.state.status === "success" && Boolean(query.meta?.persist);
    },
  },
};

// Componente ErrorBoundary para capturar erros de carregamento de chunks

const app = (
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <PersistQueryClientProvider
              client={queryClient}
              persistOptions={persistOptions}
            >
              <App />
            </PersistQueryClientProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </I18nextProvider>
  </React.StrictMode>
);

const container = document.getElementById("root");
const root = createRoot(container);
root.render(app);

// Usar unregister() para evitar caching problemático que o service worker pode causar
// especialmente em ambientes iOS
serviceWorker.unregister();
